import { Subject, from, Observable, isObservable } from 'rxjs';
import { concatMap } from 'rxjs/operators';

type QueueTask<T = any> = {
  fn: () => Promise<T> | Observable<T>;
  resolve: (value: T) => void;
  reject: (error: any) => void;
};

const fetchQueue$ = new Subject<QueueTask>();

fetchQueue$
  .pipe(
    concatMap((task) => {
      const result = task.fn();
      const obs$ = isObservable(result) ? result : from(result);

      return new Observable((subscriber) => {
        obs$.subscribe({
          next: (value) => {
            task.resolve(value);
            subscriber.complete(); // signal to concatMap to move to next task
          },
          error: (err) => {
            task.reject(err);
            subscriber.complete(); // prevent queue from stalling
          }
        });
      });
    })
  )
  .subscribe();

/**
 * Queues an API request to be executed sequentially.
 * This function ensures that API calls are processed one at a time in the order they are received,
 * preventing race conditions and managing concurrent requests.
 *
 * @template T The type of the value that the API request will resolve to
 * @param {() => Promise<T> | Observable<T>} fn A function that returns either a Promise or an Observable
 *                                              representing the API request
 * @returns {Promise<T>} A promise that resolves with the result of the API request
 */
export const queueAPIRequest = <T>(fn: () => Promise<T> | Observable<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    fetchQueue$.next({ fn, resolve, reject });
  });
